/* <orbit-carousel-3d> — featured-products 3D ring carousel (best-sellers).
   Markup + product data are rendered server-side by sections/best-sellers.liquid
   (a JSON <script class="orbit-carousel__data"> drives the ring). This element lays the coins
   out on a tipped circle, handles drag / arrows / dots / keyboard, swaps the
   name+price for the centered product, and adds that product to the cart in
   place (mirrors the theme's cart-drawer add — no checkout redirect). */
class OrbitCarousel3D extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    var dataEl = this.querySelector('.orbit-carousel__data');
    this.data = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.N = this.data.length;
    if (!this.N) return;
    this.pos = 0;
    this.active = 0;

    // ring geometry: a full circle tipped toward the viewer at the front
    this.R = 300;     // ring radius (px in z/x)
    this.RISE = 86;   // how much receding coins lift up-and-behind
    this.PER = 200;   // drag px per step
    this.DUR = 540;   // tween ms
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.stage = this.querySelector('.orbit-carousel__stage');
    this.track = this.querySelector('.orbit-carousel__track');
    this.dots = this.querySelector('.orbit-carousel__dots');
    this.text = this.querySelector('.orbit-carousel__text');
    this.name = this.querySelector('.orbit-carousel__name');
    this.price = this.querySelector('.orbit-carousel__price');
    this.cart = this.querySelector('.orbit-carousel__cart');
    this.cartLabel = this.cart ? this.cart.textContent.trim() || 'Add to Cart' : 'Add to Cart';

    var self = this;
    this.cards = this.data.map(function (p, i) {
      // each coin is a real link to its product, so the centered coin opens the
      // product page (and the browser shows its URL on hover / supports
      // open-in-new-tab). off-centre coins re-centre on click instead.
      var c = document.createElement('a');
      c.className = 'orbit-carousel__card';
      c.href = p.url || '#';
      c.draggable = false;
      // blurred low-res preview behind the coin until the full image loads
      if (p.lqip) { c.classList.add('orbit-carousel__card--lqip'); c.style.setProperty('--orbit-carousel-lqip', "url('" + p.lqip + "')"); }
      var img = document.createElement('img');
      img.className = 'orbit-carousel__coin js-fade';
      img.alt = p.name || '';
      img.draggable = false;
      img.loading = i === 0 ? 'eager' : 'lazy';
      // bind the fade before setting src so a fast/cached load isn't missed
      img.classList.add('is-loading');
      var fadeDone = (function (card) {
        return function () { img.classList.remove('is-loading'); img.classList.add('is-loaded'); card.classList.add('is-loaded'); };
      })(c);
      img.addEventListener('load', fadeDone, { once: true });
      img.addEventListener('error', fadeDone, { once: true });
      img.src = p.image;
      c.appendChild(img);
      c.addEventListener('click', function (e) {
        if (self._moved) { e.preventDefault(); return; }          // a drag, not a click
        var cur = Math.round(self.pos), act = ((cur % self.N) + self.N) % self.N;
        if (i !== act) { e.preventDefault(); self.toIndex(i); }    // off-centre → bring to front
        // centred coin: let the link follow through to the product page
      });
      self.track.appendChild(c);
      return c;
    });
    this.dotEls = this.data.map(function (p, i) {
      var d = document.createElement('button');
      d.className = 'orbit-carousel__dot';
      d.type = 'button';
      d.setAttribute('role', 'tab');
      d.setAttribute('aria-label', p.name || ('Coin ' + (i + 1)));
      d.addEventListener('click', function () { self.toIndex(i); });
      self.dots.appendChild(d);
      return d;
    });

    var prev = this.querySelector('.orbit-carousel__prev');
    var next = this.querySelector('.orbit-carousel__next');
    if (prev) prev.addEventListener('click', function () { self.go(Math.round(self.pos) - 1); });
    if (next) next.addEventListener('click', function () { self.go(Math.round(self.pos) + 1); });
    if (this.cart) this.cart.addEventListener('click', function () { self.addToCart(); });

    this.stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(Math.round(self.pos) - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(Math.round(self.pos) + 1); }
    });

    this.stage.addEventListener('pointerdown', function (e) { self.down(e); });
    addEventListener('pointermove', function (e) { self.move(e); }, { passive: true });
    addEventListener('pointerup', function () { self.up(); });
    addEventListener('pointercancel', function () { self.up(); });

    this.layout(this.pos);
    this.renderInfo(true);
    this.updateDots();
  }

  /* ---- 3D ring layout: each coin is a billboard at one angle on the circle ---- */
  layout(pos) {
    var N = this.N, R = this.R, RISE = this.RISE, step = (2 * Math.PI) / N;
    this.cards.forEach(function (card, i) {
      var th = (i - pos) * step;                 // 0 = front; ±π = directly behind
      var cos = Math.cos(th), sin = Math.sin(th);
      var x = R * sin, z = R * cos;              // front coin nearest (z = +R)
      var y = -RISE * (1 - cos) / 2;             // receding coins rise up and behind
      var front = cos * 0.5 + 0.5;               // 1 at front … 0 at back
      card.style.transform = 'translate(-50%,-50%) translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,' + z.toFixed(1) + 'px)';
      card.style.opacity = (0.16 + 0.84 * front).toFixed(3);
      card.style.pointerEvents = front < 0.4 ? 'none' : 'auto';   // back coins not clickable
    });
  }

  /* ---- arc tween: animate the rotation angle so coins keep to the real circle ---- */
  animateTo(target) {
    cancelAnimationFrame(this._tw);
    var self = this, start = this.pos, dist = target - start, t0 = performance.now();
    var ease = function (u) { return 1 - Math.pow(1 - u, 3); };
    var tick = function (now) {
      var u = Math.min(1, (now - t0) / self.DUR);
      self.pos = start + dist * ease(u);
      self.layout(self.pos);
      if (u < 1) self._tw = requestAnimationFrame(tick);
      else { self.pos = target; self.layout(self.pos); }
    };
    this._tw = requestAnimationFrame(tick);
  }

  go(target) {
    var a = ((target % this.N) + this.N) % this.N;
    if (a !== this.active) { this.active = a; this.renderInfo(); this.updateDots(); }
    this.animateTo(target);
  }
  toIndex(j) {
    var cur = Math.round(this.pos), a = ((cur % this.N) + this.N) % this.N;
    var d = j - a; d = ((d % this.N) + this.N) % this.N; if (d > this.N / 2) d -= this.N;
    this.go(cur + d);
  }

  /* ---- drag ---- */
  down(e) { cancelAnimationFrame(this._tw); this.drag = true; this._moved = false; this.startX = e.clientX; this.startPos = this.pos; }
  move(e) {
    if (!this.drag) return;
    var dx = e.clientX - this.startX;
    if (Math.abs(dx) > 5) this._moved = true;
    this.pos = this.startPos - dx / this.PER;
    var self = this;
    if (!this._raf) this._raf = requestAnimationFrame(function () { self.layout(self.pos); self._raf = 0; });
  }
  up() { if (!this.drag) return; this.drag = false; this.go(Math.round(this.pos)); }

  /* ---- info + cart target ---- */
  renderInfo(instant) {
    var p = this.data[this.active];
    var priceHTML = p.compare
      ? '<span class="orbit-carousel__sale">' + p.price + '</span><span class="orbit-carousel__was">' + p.compare + '</span>'
      : '<span class="orbit-carousel__now">' + p.price + '</span>';
    var self = this;
    var apply = function () {
      self.name.textContent = p.name;
      if (p.url) {
        self.name.innerHTML = '<a href="' + p.url + '">' + self.escape(p.name) + '</a>';
      }
      self.price.innerHTML = priceHTML;
      self.syncCart(p);
    };
    if (instant) { apply(); return; }
    clearTimeout(this._swap);
    this.text.classList.add('is-swap');
    this._swap = setTimeout(function () { apply(); self.text.classList.remove('is-swap'); }, 150);
  }

  syncCart(p) {
    if (!this.cart) return;
    this.cart.classList.remove('is-added');
    if (p.available) {
      this.cart.disabled = false;
      this.cart.textContent = this.cartLabel;
    } else {
      this.cart.disabled = true;
      this.cart.textContent = 'Sold Out';
    }
  }

  escape(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }

  updateDots() {
    this.dotEls.forEach(function (d, i) {
      var on = i === this.active;
      d.classList.toggle('on', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    }, this);
  }

  /* ---- add the centered product to the cart, in place (theme cart-drawer) ---- */
  addToCart() {
    var p = this.data[this.active];
    if (!p || !p.available || !p.variantId || this._pending) return;
    this._pending = true;

    var formData = new FormData();
    formData.append('id', p.variantId);
    formData.append('sections', ['cart-drawer', 'cart-icon-bubble']);
    formData.append('sections_url', window.location.pathname);

    var self = this;
    if (window.setButtonLoading) window.setButtonLoading(this.cart, true);
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        self._pending = false;
        if (window.setButtonLoading) window.setButtonLoading(self.cart, false);
        if (response.status) return;          // error payload (e.g. sold out)
        var drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.renderContents) drawer.renderContents(response);
        self.cart.classList.add('is-added');
        self.cart.textContent = 'Added to Cart ✓';
        clearTimeout(self._added);
        self._added = setTimeout(function () {
          self.cart.classList.remove('is-added');
          self.cart.textContent = self.cartLabel;
        }, 1600);
      })
      .catch(function () { self._pending = false; if (window.setButtonLoading) window.setButtonLoading(self.cart, false); });
  }
}
customElements.define('orbit-carousel-3d', OrbitCarousel3D);
